/**
 * WebHID-based joystick input — an alternative to the Gamepad API for users
 * hitting its ~4-simultaneous-device cap (a real, documented Chromium/Windows
 * limitation multiplexing XInput+DirectInput into the Gamepad API's device
 * list; not something fixable from a web page using that API). WebHID talks
 * to each USB HID device directly, with no such cap, at the cost of the user
 * having to explicitly grant access per device (browser security requirement
 * for raw HID access) and us having to parse each device's report descriptor
 * ourselves — there's no built-in "axes"/"buttons" abstraction like Gamepad
 * API gives for free.
 *
 * Report parsing is generic/descriptor-driven (reads logical min/max, usage
 * pages, report sizes off HIDDevice.collections) rather than hardcoded per
 * device model, so it should work with any standard joystick/HOTAS/button-box
 * HID descriptor: Generic Desktop page (0x01) axis usages (X/Y/Z/Rx/Ry/Rz/
 * Slider/Dial/Wheel, usage 0x30-0x38) become normalised -1..1 axes in
 * descriptor order; Button page (0x09) usages become boolean buttons. Hat
 * switches (Generic Desktop usage 0x39, a discrete POV control) aren't
 * mapped to anything yet.
 */

const GENERIC_DESKTOP_PAGE = 0x01;
const BUTTON_PAGE = 0x09;
const AXIS_USAGES = new Set([0x30, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38]); // X Y Z Rx Ry Rz Slider Dial Wheel

/** Shape-compatible with the subset of the native Gamepad interface our
 * input code actually reads (id/axes/buttons), so Gamepad.ts and
 * GamepadConfig's readAxis/readButton can consume either source uniformly. */
export interface PseudoGamepad {
  id: string;
  axes: number[];
  buttons: { pressed: boolean }[];
}

interface FieldMap {
  reportId: number;
  bitOffset: number;
  reportSize: number;
  reportCount: number;
  logicalMinimum: number;
  logicalMaximum: number;
  kind: 'axis' | 'button';
  /** Index into the pseudo-gamepad's axes[]/buttons[] this field's first
   * repetition writes to (subsequent repetitions, if reportCount > 1, use
   * consecutive indices). */
  outIndex: number;
  /** Button fields only: some devices (interface boards like the BU0836X are
   * a common example) report buttons as a "usage array" rather than a
   * bitmap — each of reportCount slots holds a button NUMBER (0 = none
   * pressed in that slot) instead of one bit per button. These need very
   * different decoding: a bitmap field's Nth bit IS button N's state, but
   * an array field's Nth slot VALUE says which button (if any) occupies
   * that slot this report, so the whole button range must be cleared and
   * only the reported slots' button numbers set, every report. */
  isArray?: boolean;
  usageIdMin?: number;
  arraySpan?: number;
}

interface ParsedDevice {
  device: HIDDevice;
  fields: FieldMap[];
  axisCount: number;
  buttonCount: number;
  pad: PseudoGamepad;
}

export class WebHidInput {
  private parsed = new Map<HIDDevice, ParsedDevice>();
  /** Stable slot order (insertion order), matching how Gamepad.ts indexes
   * native pads by array position. */
  private order: HIDDevice[] = [];

  get supported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.hid;
  }

  constructor() {
    if (!this.supported) return;
    navigator.hid!.addEventListener('connect', (e) => { void this.attach(e.device); });
    navigator.hid!.addEventListener('disconnect', (e) => {
      this.parsed.delete(e.device);
      this.order = this.order.filter((d) => d !== e.device);
    });
    // Devices the user already granted access to in a previous session persist
    // across reloads (per-origin permission), so reattach them automatically.
    navigator.hid!.getDevices().then((devices) => {
      for (const d of devices) void this.attach(d);
    }).catch(() => { /* ignore */ });
  }

  /** Must be called from a user gesture (click) — shows the browser's device
   * picker. Resolves once any newly-granted devices are opened and parsed. */
  async requestDevice(): Promise<void> {
    if (!this.supported) throw new Error('WebHID is not supported in this browser.');
    const devices = await navigator.hid!.requestDevice({ filters: [] });
    for (const d of devices) await this.attach(d);
  }

  private async attach(device: HIDDevice): Promise<void> {
    if (this.parsed.has(device)) return;
    try {
      if (!device.opened) await device.open();
    } catch {
      return; // device busy/denied — skip it, don't crash the whole list
    }
    const fields = parseFields(device.collections);
    // Each field can cover MULTIPLE slots (reportCount > 1 for a bitmap of
    // several buttons in one field, or arraySpan for a usage-array field) —
    // sizing by "outIndex + 1" instead of "outIndex + span" left the axes/
    // buttons arrays far too short whenever a field wasn't a single lone
    // value, e.g. the extremely common "32 buttons in one field" case,
    // silently dropping every button past the first (writes past the
    // array's actual length landed on `undefined`, throwing inside the
    // inputreport handler — no crash, no button response).
    const spanOf = (f: FieldMap) => (f.kind === 'button' && f.isArray ? f.arraySpan ?? 1 : f.reportCount);
    const axisCount = fields.filter((f) => f.kind === 'axis').reduce((n, f) => Math.max(n, f.outIndex + spanOf(f)), 0);
    const buttonCount = fields.filter((f) => f.kind === 'button').reduce((n, f) => Math.max(n, f.outIndex + spanOf(f)), 0);
    const pad: PseudoGamepad = {
      id: `${device.productName || 'HID device'} (WebHID)`,
      axes: new Array(axisCount).fill(0),
      buttons: Array.from({ length: buttonCount }, () => ({ pressed: false })),
    };
    const entry: ParsedDevice = { device, fields, axisCount, buttonCount, pad };
    this.parsed.set(device, entry);
    this.order.push(device);
    device.addEventListener('inputreport', (ev) => this.onReport(entry, ev));
  }

  private onReport(entry: ParsedDevice, ev: HIDInputReportEvent): void {
    for (const f of entry.fields) {
      if (f.reportId !== ev.reportId) continue;

      if (f.kind === 'button' && f.isArray) {
        // Usage-array buttons: the field only ever lists which slots are
        // CURRENTLY pressed, so every button in its range must be cleared
        // first, then re-set from this report's slot values.
        const span = f.arraySpan ?? 0;
        for (let i = 0; i < span; i++) entry.pad.buttons[f.outIndex + i].pressed = false;
        for (let rep = 0; rep < f.reportCount; rep++) {
          const raw = readBits(ev.data, f.bitOffset + rep * f.reportSize, f.reportSize);
          const bi = raw - (f.usageIdMin ?? 0);
          if (bi >= 0 && bi < span) entry.pad.buttons[f.outIndex + bi].pressed = true;
        }
        continue;
      }

      const range = f.logicalMaximum - f.logicalMinimum || 1;
      for (let rep = 0; rep < f.reportCount; rep++) {
        const raw = readBits(ev.data, f.bitOffset + rep * f.reportSize, f.reportSize);
        if (f.kind === 'axis') {
          const norm = ((raw - f.logicalMinimum) / range) * 2 - 1;
          entry.pad.axes[f.outIndex + rep] = Math.max(-1, Math.min(1, norm));
        } else {
          entry.pad.buttons[f.outIndex + rep].pressed = raw !== 0;
        }
      }
    }
  }

  /** All currently attached/parsed devices as pseudo-gamepads, in stable slot
   * order — mirrors navigator.getGamepads()'s array shape closely enough for
   * Gamepad.ts to treat either source identically. */
  getPads(): (PseudoGamepad | null)[] {
    return this.order.map((d) => this.parsed.get(d)?.pad ?? null);
  }
}

/** Read `bits` bits starting at bit offset `start` from a HID report body,
 * LSB-first within each byte and across byte boundaries (per the HID spec's
 * bit-packing rule), as an unsigned integer. */
function readBits(data: DataView, start: number, bits: number): number {
  let value = 0;
  for (let i = 0; i < bits; i++) {
    const bitPos = start + i;
    const byte = data.getUint8(bitPos >> 3);
    const bit = (byte >> (bitPos & 7)) & 1;
    value |= bit << i;
  }
  return value;
}

/** Walk a device's parsed report descriptor (collections -> input reports ->
 * items) and build a flat, ordered field map with each field's bit offset
 * within its report — reconstructing what a manual descriptor parser would
 * give us, since WebHID exposes the descriptor pre-parsed rather than raw. */
function parseFields(collections: HIDCollectionInfo[]): FieldMap[] {
  const fields: FieldMap[] = [];
  const bitOffsetByReport = new Map<number, number>();
  let axisIndex = 0;
  let buttonIndex = 0;

  const visit = (cols: HIDCollectionInfo[]) => {
    for (const col of cols) {
      for (const report of col.inputReports) {
        let offset = bitOffsetByReport.get(report.reportId) ?? 0;
        for (const item of report.items) {
          const usagePage = item.isRange
            ? (item.usageMinimum ?? 0) >>> 16
            : (item.usages[0] ?? 0) >>> 16;
          const usageId = item.isRange ? (item.usageMinimum ?? 0) & 0xffff : (item.usages[0] ?? 0) & 0xffff;

          if (!item.isConstant && usagePage === GENERIC_DESKTOP_PAGE && AXIS_USAGES.has(usageId)) {
            fields.push({
              reportId: report.reportId,
              bitOffset: offset,
              reportSize: item.reportSize,
              reportCount: item.reportCount,
              logicalMinimum: item.logicalMinimum,
              logicalMaximum: item.logicalMaximum,
              kind: 'axis',
              outIndex: axisIndex,
            });
            axisIndex += item.reportCount;
          } else if (!item.isConstant && usagePage === BUTTON_PAGE) {
            if (item.isArray) {
              const idMin = item.isRange ? (item.usageMinimum ?? 0) & 0xffff : usageId;
              const idMax = item.isRange ? (item.usageMaximum ?? idMin) & 0xffff : idMin;
              const span = Math.max(idMax - idMin + 1, item.logicalMaximum - item.logicalMinimum + 1, 1);
              fields.push({
                reportId: report.reportId,
                bitOffset: offset,
                reportSize: item.reportSize,
                reportCount: item.reportCount,
                logicalMinimum: item.logicalMinimum,
                logicalMaximum: item.logicalMaximum,
                kind: 'button',
                outIndex: buttonIndex,
                isArray: true,
                usageIdMin: idMin,
                arraySpan: span,
              });
              buttonIndex += span;
            } else {
              fields.push({
                reportId: report.reportId,
                bitOffset: offset,
                reportSize: item.reportSize,
                reportCount: item.reportCount,
                logicalMinimum: item.logicalMinimum,
                logicalMaximum: item.logicalMaximum,
                kind: 'button',
                outIndex: buttonIndex,
              });
              buttonIndex += item.reportCount;
            }
          }
          // Constant/padding fields, hat switches, and anything else still
          // occupy bits — always advance the offset even when not mapped.
          offset += item.reportSize * item.reportCount;
        }
        bitOffsetByReport.set(report.reportId, offset);
      }
      if (col.children.length) visit(col.children);
    }
  };
  visit(collections);
  return fields;
}
